import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "@/constants";
import { ANALYTICS_STALE_TIME } from "../../components/analytics/constants/constants";
import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  ArrowSquareOutIcon,
  EyeSlashIcon,
  TrendUpIcon,
} from "@phosphor-icons/react";
import { useCustom, useGo, useTranslate } from "@refinedev/core";
import {
  Button,
  Card,
  Col,
  Flex,
  Grid,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { Empty } from "components/Empty/";
import dayjs from "dayjs";
import { useAntTheme } from "hooks/useAntTheme";
import React from "react";
import { useParams } from "react-router-dom";
import {
  ChatSummaryStats,
  DocumentSummaryStats,
  VideoSummaryStats,
} from "types/";
import { ProjectAnalyticsStatCard } from "./ProjectAnalyticsStatCard";
import { ProjectAnalyticsStatCardSkeleton } from "./ProjectAnalyticsStatCardSkeleton";
import { radius } from "@libertify/ui";

const { Title } = Typography;

interface IProjectDetails {
  projectId: string;
  instanceId: string;
  storyboardId: string;
  isLive: boolean;
  creationDate: string;
}

interface ProjectAnalyticsProps {
  projectDetails: IProjectDetails;
  disablePublish: boolean;
}
const roundNumber = (
  value: number | undefined | null,
  decimals = 0
): number | null => {
  if (value == null || Number.isNaN(value)) return null;
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export const ProjectAnalytics: React.FC<ProjectAnalyticsProps> = ({
  projectDetails,
  disablePublish,
}) => {
  const go = useGo();
  const { theme } = useAntTheme();
  const params = useParams();
  const t = useTranslate();
  const breakpoint = Grid.useBreakpoint();
  const currentProjectId = projectDetails.projectId || params.id;
  const minDate = dayjs().subtract(ANALYTICS_MAX_DATE_RANGE_MONTHS, "month");
  const projectCreationDate = dayjs(projectDetails.creationDate);
  const startDate = (
    projectCreationDate.isAfter(minDate) ? projectCreationDate : minDate
  ).format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  const enabled = !!currentProjectId && !!projectDetails.creationDate;

  const { data: videoSummary, isFetching: isVideoFetching } =
    useCustom<VideoSummaryStats>({
      url: `analytics/projects/${currentProjectId}/analytics/video/summary`,
      method: "get",
      queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
      errorNotification: false,
    });

  const { data: documentSummary, isFetching: isDocumentFetching } =
    useCustom<DocumentSummaryStats>({
      url: `analytics/projects/${currentProjectId}/analytics/document/summary`,
      method: "get",
      queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
      errorNotification: false,
    });

  const { data: chatSummary, isFetching: isChatFetching } =
    useCustom<ChatSummaryStats>({
      url: `analytics/projects/${currentProjectId}/analytics/chat/summary`,
      method: "get",
      queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
      errorNotification: false,
    });

  const videoData = videoSummary?.data;
  const documentData = documentSummary?.data;
  const chatData = chatSummary?.data;
  const allFetching = isVideoFetching || isDocumentFetching || isChatFetching;
  const hasNoData = !allFetching && !videoData && !documentData && !chatData;

  const handleOpenAnalytics = () => {
    go({
      to: `/media/projects/${currentProjectId}/analytics`,
    });
  };

  const renderAnalyticsContent = () => {
    if (!projectDetails.isLive && hasNoData) {
      return (
        <Card style={{ backgroundColor: "#FFE8E9", border: 0 }}>
          <Flex align="center">
            <EyeSlashIcon size={48} style={{ color: theme.colorGrey }} />
            <Typography.Text
              style={{
                marginLeft: "8px",
                color: theme.colorGrey,
                fontSize: "16px",
                lineHeight: 1,
                letterSpacing: "-0.72px",
              }}
            >
              {t("media.components.ProjectAnalytics.unavailableAnalytics")}
            </Typography.Text>
          </Flex>
        </Card>
      );
    }

    if (hasNoData) {
      return (
        <Empty
          size={100}
          image={<TrendUpIcon size={50} />}
          description={
            <>
              <Typography.Title level={5}>
                {t("media.components.ProjectAnalytics.noDataTitle")}
              </Typography.Title>
              <Typography.Text>
                {t("media.components.ProjectAnalytics.noDataDescription")}
              </Typography.Text>
            </>
          }
        />
      );
    }

    return (
      <Row gutter={[10, 10]}>
        <>
          <Col xs={24} sm={8}>
            <ProjectAnalyticsStatCard
              title={t("media.components.ProjectAnalytics.video.label")}
              percentage={roundNumber(videoData?.avg_completion_pct)}
              count={videoData?.impression_sessions ?? null}
              label={t("media.components.ProjectAnalytics.video.countLabel")}
              color={theme.colorPrimary}
              percentageTooltip={t(
                "media.projects.analytics.dashboard.tooltips.videoCompletionRate"
              )}
            />
          </Col>

          <Col xs={24} sm={8}>
            <ProjectAnalyticsStatCard
              title={t("media.components.ProjectAnalytics.document.label")}
              percentage={roundNumber(documentData?.avg_reading_depth_pct)}
              count={documentData?.completed_sessions ?? null}
              label={t("media.components.ProjectAnalytics.document.countLabel")}
              color="#FA9EBC"
              percentageTooltip={t(
                "media.projects.analytics.dashboard.tooltips.percentRead"
              )}
            />
          </Col>

          <Col xs={24} sm={8}>
            <ProjectAnalyticsStatCard
              title={t("media.components.ProjectAnalytics.chatbot.label")}
              percentage={roundNumber(chatData?.chat_rate_pct)}
              count={chatData?.avg_messages_sent ?? null}
              label={t("media.components.ProjectAnalytics.chatbot.countLabel")}
              color="#FFBE98"
              percentageTooltip={t(
                "media.projects.analytics.dashboard.tooltips.chatEngagement"
              )}
            />
          </Col>
        </>
      </Row>
    );
  };

  return (
    <Card
      style={{
        borderRadius: radius.box,
        border: theme.cardBorder,
        backgroundColor: theme.colorCardRedesign,
        boxShadow: theme.cardBoxShadow,
        width: "100%",
      }}
      styles={{
        body: { padding: 25 },
      }}
    >
      <>
        <Row
          align={"middle"}
          justify="space-between"
          gutter={[8, 8]}
          style={{ marginBottom: 16 }}
        >
          <Col sm={{ span: 24 }} lg={24} xl={16}>
            <Space>
              <Title
                level={2}
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "500",
                  color: theme.colorTextTitle,
                }}
              >
                {t("media.components.ProjectAnalytics.title")}
              </Title>
              <Tooltip
                title={t("media.components.ProjectAnalytics.from", {
                  startDate: dayjs(startDate).format("LL"),
                  endDate: dayjs(endDate).format("LL"),
                })}
                styles={{ body: { fontSize: 11 } }}
              >
                <QuestionCircleOutlined
                  style={{
                    fontSize: 14,
                    color: theme.colorTextTertiary,
                  }}
                />
              </Tooltip>
            </Space>
          </Col>

          {projectDetails.isLive && !hasNoData && !allFetching && (
            <Col sm={{ span: 24 }} lg={24} xl={8}>
              <Row justify={breakpoint.xl ? "end" : "start"}>
                <Button
                  type="primary"
                  icon={
                    <ArrowSquareOutIcon
                      size={20}
                      style={{ verticalAlign: "text-top" }}
                    />
                  }
                  size="middle"
                  disabled={!projectDetails.isLive || disablePublish}
                  onClick={handleOpenAnalytics}
                  shape="round"
                >
                  {t("media.components.ProjectAnalytics.openAnalytics")}
                </Button>
              </Row>
            </Col>
          )}
        </Row>
        {allFetching && <ProjectAnalyticsStatCardSkeleton />}
        {!allFetching && renderAnalyticsContent()}
      </>
    </Card>
  );
};
